/* eslint camelcase: 0 */
import { useLazyQuery } from '@apollo/client';
import { QueryMode, User } from 'lib/generated/graphql';
import { USER_GET_USERS } from 'lib/queries';
import Image from 'next/image';
import { useState } from 'react';
import AsyncSelect from 'react-select/async';
import Creatable from 'react-select/creatable';
import * as Yup from 'yup';

export const SearchForUser = ({ onSelectUser }: { onSelectUser: (user: User) => void }) => {
  const [_, setSearchValue] = useState('');
  const [selectedCollaborator, setSelectedCollaborator] = useState<User | null>(null);

  // loading user data
  const [getUsers] = useLazyQuery(USER_GET_USERS);

  const loadOptions = async (inputValue: string, callback: (options: any) => void) => {
    const result = await getUsers({
      variables: { getUsersWhere: { name: { startsWith: inputValue, mode: QueryMode.Insensitive } } },
    });
    return result?.data?.getAllUsers?.users || [];
  };

  // input handling
  const handleInputChange = async (newValue: string) => setSearchValue(newValue);
  const getOptionLabel = (option: User) => option.name!;
  const onSelect = (user: User) => {
    setSelectedCollaborator(user);
    onSelectUser(user);
    setSelectedCollaborator(null); // reset so user can add more, may need to customize if common component (call reset func?)
  };

  // styles for react-select
  const customStyles = {
    control: styles => ({
      ...styles,
      backgroundColor: '#C4C4C433',
      border: 'transparent',
      cursor: 'text',
    }),
    input: styles => ({
      ...styles,
      "input[type='text']:focus": { boxShadow: 'none' },
    }),
  };

  return (
    <AsyncSelect
      value={selectedCollaborator}
      styles={customStyles}
      cacheOptions
      backspaceRemovesValue={false}
      openMenuOnClick={false}
      loadOptions={loadOptions}
      defaultOptions
      onInputChange={handleInputChange}
      getOptionLabel={getOptionLabel}
      controlShouldRenderValue={false}
      isClearable
      onChange={onSelect}
      placeholder="Search for Huddle User"
      components={{ DropdownIndicator: () => null, IndicatorSeparator: () => null }}
      formatOptionLabel={user => (
        <div className="ml-2 flex justify-start gap-5">
          <Image className="rounded" height={20} width={25} src={user.photoUrl || process.env.DEFAULT_USER_AVATAR} />
          <span>{user.name}</span>
        </div>
      )}
    />
  );
};

export const AddUserByEmail = ({ addEmail }: any) => {
  const isValid = (email: string) => {
    if (!email) return false;
    return Yup.string().email().isValidSync(email);
  };

  const onSelect = email => addEmail(email.value);

  const customStyles = {
    control: styles => ({
      ...styles,
      backgroundColor: '#C4C4C433',
      border: 'transparent',
      cursor: 'text',
    }),
    input: styles => ({
      ...styles,
      "input[type='text']:focus": { boxShadow: 'none' },
    }),
    noOptionsMessage: styles => ({
      display: 'none',
    }),
  };

  return (
    <Creatable
      value={null}
      styles={customStyles}
      placeholder="Enter an email to invite someone"
      components={{ DropdownIndicator: () => null, IndicatorSeparator: () => null }}
      formatCreateLabel={value => `Add email ${value}`}
      controlShouldRenderValue={false}
      isValidNewOption={isValid}
      openMenuOnClick={false}
      backspaceRemovesValue={false}
      onChange={onSelect}
    />
  );
};
